// Modules
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  ADMINISTRATOR_SERVICES,
  AUTH_SERVICES,
  BILLING_ROLES,
  CLIENT_AUTH_SERVICES,
  CLIENT_ROLES,
  EXTERNAL_AUTH_ROLES,
  PRAECE_ROLES,
  PERSON_ROLES,
  SUPER_USER_SERVICES
} from 'constants';
import { CompanySelect, TeamSelect } from 'components/forms/selects';
import { Form, Row, SubformHeader, Subform } from 'components/forms/form-structure';
import { TextInput, SelectInput, CheckboxGroupInput, CheckboxInput } from 'components/forms/form-elements';
import { convertArrayToOptions } from 'utils/input-utils';
import AddressForm from 'components/models/person/address-form';
import EmailForm from 'components/models/person/email-form';
import Person from 'services/data/person';
import PhoneNumberForm from 'components/models/person/phone-number-form';
import Tooltip from 'components/widgets/overlays/tooltip';
import requiredValidation from 'utils/required-validation';


// Component
function PersonForm({ formValues, change, currentUser, initialValues, companies, quickAdd, settings, ...props }) {
  const isPraece = Person.isPraece(formValues);
  const company = _.find(companies, { id: formValues.company });
  const isPraeceCompany = _.get(company, 'id') === 1;
  const isUser = Person.isUser(formValues);
  const isLead = Person.isLead(currentUser);
  const isAdmin = Person.isAdmin(currentUser);
  const roles = _.difference(PERSON_ROLES, isPraeceCompany ? CLIENT_ROLES : PRAECE_ROLES);
  const demoting = formValues.role === 'Contact' && initialValues && initialValues.role && initialValues.role !== 'Contact';

  if (quickAdd) {
    return (
      <Form onSubmit={props.handleSubmit} {..._.pick(props, ['submitting', 'error', 'submitFailed'])}>
        <Row label="Name">
          <Field component={TextInput} name="firstName" placeholder="first" />
          <Field component={TextInput} name="lastName" placeholder="last" />
        </Row>
        <Field component={CompanySelect} name="company" label="Company" />
        <EmailForm change={change} allowPrimary />
        <PhoneNumberForm />
      </Form>
    );
  }

  return (
    <Form onSubmit={props.handleSubmit} {..._.pick(props, ['submitting', 'error', 'submitFailed'])}>
      <Row label="Name">
        <Field component={TextInput} name="firstName" placeholder="first" />
        <Field component={TextInput} name="lastName" placeholder="last" />
      </Row>
      <Field component={CompanySelect} name="company" label="Company" />
      { isLead && company && <Field component={SelectInput} name="role" label="Security Role" options={convertArrayToOptions(roles)} /> }
      { isPraece && isLead &&
        <div>
          <div className="form-flex-row">
            <Field component={TeamSelect} name="team" label="Team" />
            <Field component={TextInput} name="poPrefix" label="PO Prefix" />
            <Field component={SelectInput} name="billingRole" label="Billing Role" options={convertArrayToOptions(BILLING_ROLES)} />
          </div>
          <Field component={CheckboxInput} label="On Call" name="onCall" />
        </div>
      }
      { demoting && <div className="danger-block notification-block">Warning: Making this user a contact will remove all app roles, blocking their access to the portal and all other Praece apps!</div> }
      { isUser && ((!isPraece && isLead) || isAdmin) &&
        <Subform>
          <SubformHeader>App Roles</SubformHeader>
          <div className="flex external-auth-role-select">
            { _.map(isPraece ? AUTH_SERVICES : CLIENT_AUTH_SERVICES, service => {
              const connected = _.includes(_.keys(company.externalAppIds), service);
              const options = _.map(convertArrayToOptions(EXTERNAL_AUTH_ROLES), option => _.assign({ disabled: !connected && !isPraece }, option));

              if (!_.includes(SUPER_USER_SERVICES, service)) _.remove(options, { value: 'Super User' });
              if (!_.includes(ADMINISTRATOR_SERVICES, service)) _.remove(options, { value: 'Administrator' });

              const field = <Field key={service} component={CheckboxGroupInput} name={`externalAppAuthRoles[${service}]`} label={_.startCase(service)} options={options} />;

              if (connected || isPraece) return field;

              return <Tooltip key={service} title={`You must connect ${_.startCase(service)} for this client before you can give users access!`}>{field}</Tooltip>;
            }) }
          </div>
        </Subform>
      }
      { settings.personTags && !isPraeceCompany &&
        <Subform>
          <SubformHeader>Tags</SubformHeader>
          <Field component={CheckboxGroupInput} name="tags" options={convertArrayToOptions(settings.personTags)} />
        </Subform>
      }
      <EmailForm change={change} allowPrimary />
      <PhoneNumberForm />
      <AddressForm />
    </Form>
  );
}

PersonForm.propTypes = {
  formValues: PropTypes.object,
  change: PropTypes.func,
  currentUser: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  companies: PropTypes.array,
  initialValues: PropTypes.object,
  quickAdd: PropTypes.bool,
  settings: PropTypes.object
};

const ConnectedForm = connect((state, ownProps) => ({
  formValues: formValueSelector(ownProps.form)(state, 'company', 'role', 'emailAddresses'),
  companies: state.pageData.selectCompanies,
  currentUser: state.auth.currentUser,
  settings: state.pageData.settings
}))(PersonForm);

export default reduxForm({
  form: 'person-form',
  validate: (values) => {
    const errors = {};
    const isPraece = Person.isPraece(values);
    const isUser = Person.isUser(values);

    const required = requiredValidation(['firstName', 'lastName', 'role'], values);
    const userRequired = requiredValidation(['company'], values);
    const praeceRequired = requiredValidation(['team', 'poPrefix', 'billingRole'], values);

    errors.emailAddresses = _.map(values.emailAddresses, email => requiredValidation(['email', 'type'], email));
    errors.phoneNumbers = _.map(values.phoneNumbers, number => requiredValidation(['number', 'type'], number));
    errors.addresses = _.map(values.addresses, address => requiredValidation(['address', 'city', 'state', 'zip', 'type'], address));

    if (!_.some(values.emailAddresses, { primary: true }) && isUser) errors.emailAddresses._error = 'A primary email is required!';

    if (!_.some(values.phoneNumbers, { type: 'Mobile' }) && isPraece) errors.phoneNumbers._error = 'A mobile number is required for Praece users for triage calls!';

    const allErrors = _.assign(errors, required, isUser ? userRequired : {}, isPraece ? praeceRequired : {});

    return allErrors;
  }
})(ConnectedForm);
